import type { TFunction } from 'i18next';
import { CCModelUtility, CCUtilityAssignments, ModelRegistryType } from '../../types';
import { getModelValueLabel } from '../../constants/modelOptions';

export interface UtilityDefinition {
  key: CCModelUtility;
  label: string;
  type: ModelRegistryType;
  description: string;
}

/**
 * The three utility slots a domain's config maps onto — LLM utilities
 * (Answering, Judge) and the single Embedding utility. Matches
 * design-frames/12-landing-models.html's UTILITIES constant. Labels are
 * resolved through i18next so they follow the active locale.
 */
export function utilities(t: TFunction): UtilityDefinition[] {
  return [
    { key: 'answering', label: t('models.utilities.answering'), type: 'llm', description: '' },
    { key: 'judge', label: getModelValueLabel('judge', t), type: 'llm', description: '' },
    { key: 'embedding', label: getModelValueLabel('embedding', t), type: 'embedding', description: '' },
  ];
}

/**
 * Replace raw utility names (answering/judge/embedding) with their UI display
 * labels in a free-text string — used to give backend error/detail messages
 * label parity with the rest of the UI (e.g. "judge" -> "Evaluation").
 */
export function labelizeUtilityNames(text: string, t: TFunction): string {
  return utilities(t).reduce(
    (acc, u) => acc.replace(new RegExp(`\\b${u.key}\\b`, 'g'), u.label),
    text,
  );
}

/**
 * PUT .../utility-assignments is a whole replace — a slot this dialog never
 * shows (e.g. Embedding, while editing an LLM configuration) must still be
 * sent back unchanged, or saving would silently clear it. Builds the full
 * next-state triple from the org's current assignments plus only the
 * slots eligible for `editingId`'s type: checked -> claim it for
 * `editingId`; unchecked -> release it only if `editingId` currently holds
 * it (an unchecked slot already owned by some other configuration is left
 * untouched, never blanked).
 */
export function mergeUtilitySelection(
  current: CCUtilityAssignments,
  editingId: string,
  eligibleUtilities: UtilityDefinition[],
  utilitySelected: Partial<Record<CCModelUtility, boolean>>,
): CCUtilityAssignments {
  const next: CCUtilityAssignments = { ...current };
  eligibleUtilities.forEach(({ key }) => {
    if (utilitySelected[key]) {
      next[key] = editingId;
    } else if (next[key] === editingId) {
      next[key] = null;
    }
  });
  return next;
}
